import { useState } from 'react';
import { ProductType } from '../../../../../models/product-type.enum';
import { TravellerCategory } from '../../../../../models/traveller-category.enum';
import { Channel } from '../../../../../models/channel.enum';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import { ConfigurationService } from '../../../../../services/configuration.service';
import { IdHelper } from '../../../../../helpers/id.helper';
import SalesChannelPrice from '../SalesChannelPrice';
import GroupedTicketSuggestions from '../../models/ticket-suggestion-group';
import type SingleTicketGrouping from '../../models/single-ticket-grouping';

function ProductTypeTable({
  ticketSuggestions,
  currentTravellerCategory,
  productType,
}: {
  ticketSuggestions: GroupedTicketSuggestions[];
  currentTravellerCategory: TravellerCategory;
  productType: ProductType;
}) {
  // === STATE ===
  const [selectedIndex, setSelectedIndex] = useState(0);

  // === CONSTANTS ===
  const translationNumberOfTicketSuggestions = TranslationService.get('ticketsuggestions.numberof');
  const translationWhereToBuyHeading = TranslationService.get('ticketsuggestions.wheretobuyheading');
  const translationSingleTicket = TranslationService.get('travelplanner.singleticket');
  const translationPrePaidTicket = TranslationService.get('travelplanner.prepaidticket');
  const translationValid = TranslationService.get('travelplanner.valid');
  const translationPeriodTicket = TranslationService.get('travelplanner.periodticket');
  const translationFlexTicket = TranslationService.get('travelplanner.flexticket');
  const translationDayTicket = TranslationService.get('travelplanner.dayticket');
  const translationSummerTicket = TranslationService.get('travelplanner.summerticket');
  const translationNoTicketSuggestionsAvailable = TranslationService.get('common.noticketsuggestionsavailabletext');
  const translationPrice = TranslationService.get('common.price');
  const translationTableCaption = TranslationService.get('ticketsuggestions.tablecaption');
  const singleTicketProductType = ProductType.singleTicket;

  // === VARIABLES ===
  const productTypeId = IdHelper.newUniqueId('product-type-');
  const showTapAndGoInTravelPlanner = ConfigurationService.get('travelplanner.enabletapandgosaleschannelintravelplannerfeatureflag') === 'true';
  const filteredTicketSuggestions = ticketSuggestions
    .filter((x) => x.hasChannelPrice(currentTravellerCategory))
    .sort((a, b) => a.getComparePrice(currentTravellerCategory) - b.getComparePrice(currentTravellerCategory));
  const groupedSingleTickets = ticketSuggestions[0].singleTicketGroupings;

  // === FUNCTIONS ===
  function getName(index: number) {
    const ticketSuggestion = filteredTicketSuggestions[index];
    if (!ticketSuggestion) return '';
    if (productType === ProductType.prepaidTicket) return `${translationPrePaidTicket} ${translationValid} ${ticketSuggestion.validityTimeMinutes} min`;
    if (productType === ProductType.periodTicket) return `${translationPeriodTicket} ${ticketSuggestion.name}`;
    if (productType === ProductType.flexTicket) return `${translationFlexTicket} ${ticketSuggestion.name}`;
    if (productType === ProductType.dayTicket) return `${translationDayTicket} ${ticketSuggestion.name}`;
    if (productType === ProductType.partialYearTicket) return ticketSuggestion.name?.replace('Sommarbiljett', translationSummerTicket);
    return ticketSuggestion.name;
  }

  function getSingleTicketName(ticket: SingleTicketGrouping) {
    if (ticket.productInstanceType == null || ticket.productInstanceType === 'dynamic') return `${translationSingleTicket} ${ticket.ticketName} ${ticket.validity} min`;

    return `${translationSingleTicket} ${ticket.ticketName}`;
  }

  function getSelectAriaLabel() {
    if (productType === ProductType.singleTicket) return `Select ${translationSingleTicket}`;
    if (productType === ProductType.prepaidTicket) return `Select ${translationPrePaidTicket}`;
    if (productType === ProductType.periodTicket) return `Select ${translationPeriodTicket}`;
    if (productType === ProductType.dayTicket) return `Select ${translationDayTicket}`;
    if (productType === ProductType.partialYearTicket) return `Select ${translationSummerTicket}`;
    return 'Select';
  }

  function getAnalyticsAction() {
    if (productType === ProductType.periodTicket) return 'Periodbiljett - Köp din biljett här';
    return 'Dygnsbiljett - Köp din biljett här';
  }

  function getAnalyticsLabel(salesChannel: Channel) {
    if (salesChannel === Channel.ombud) return 'Ombud';
    if (salesChannel === Channel.ombord) return 'Ombord';
    return 'To Go';
  }

  // === EVENT HANDLERS ===
  function handleSalesChannelClick(salesChannel: Channel): void {
    if (productType !== ProductType.singleTicket) {
      const analyticsAction = getAnalyticsAction();
      const analyticsLabel = getAnalyticsLabel(salesChannel);
      AnalyticsService.addEvent(AnalyticsService.FindTicket, analyticsAction, analyticsLabel);
    }
  }

  return (
    <div className="product-type-table">
      {productType === singleTicketProductType && (
        <>
          {filteredTicketSuggestions.length > 0 && (
            <>
              {groupedSingleTickets.map((ticket) => {
                if (currentTravellerCategory === ticket.travellerCategory && ticket.salesChannels.length > 0) {
                  return (
                    <div key={ticket.ticketName}>
                      <div className="h4 pt-3">{getSingleTicketName(ticket)}</div>
                      <table className="w-100">
                        <caption className="visually-hidden">
                          {translationTableCaption} {getSingleTicketName(ticket)}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col" className="text-reset pb-2">
                              {translationWhereToBuyHeading}:
                            </th>
                            <th scope="col" className="visually-hidden">
                              {translationPrice}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {ticket.salesChannels.map((salesChannelPrice) => {
                            if (!(salesChannelPrice.salesChannel === 14 && !showTapAndGoInTravelPlanner)) {
                              return <SalesChannelPrice key={salesChannelPrice.salesChannel.toString()} salesChannelPrice={salesChannelPrice} onClick={handleSalesChannelClick} />;
                            }
                            return false;
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return false;
              })}
            </>
          )}
          {filteredTicketSuggestions.length === 0 && <div className="mt-3">{translationNoTicketSuggestionsAvailable}</div>}
        </>
      )}
      {productType !== singleTicketProductType && (
        <>
          {filteredTicketSuggestions.length > 0 && (
            <>
              {filteredTicketSuggestions.length > 1 && (
                <div className="py-3">
                  <p id={productTypeId} className="fw-normal">
                    <span className="fw-bold">{filteredTicketSuggestions.length}</span> {translationNumberOfTicketSuggestions}
                  </p>
                  <select
                    className="form-select fw-bold text-truncate"
                    value={selectedIndex}
                    onChange={(e) => setSelectedIndex(Number(e.target.value))}
                    aria-label={getSelectAriaLabel()}
                    aria-describedby={productTypeId}
                  >
                    {filteredTicketSuggestions.map((_, index) => (
                      <option key={index} value={index}>
                        {getName(index)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {filteredTicketSuggestions.length === 1 && <div className="h4 pt-3">{getName(selectedIndex)}</div>}
              <table className="w-100">
                <caption className="visually-hidden">
                  {translationTableCaption} {getName(selectedIndex)}
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="text-reset pb-2">
                      {translationWhereToBuyHeading}:
                    </th>
                    <th scope="col" className="visually-hidden">
                      {translationPrice}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTicketSuggestions[selectedIndex] && (
                    <>
                      {currentTravellerCategory === TravellerCategory.adult && filteredTicketSuggestions[selectedIndex].adultTogoSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].adultTogoSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                      {currentTravellerCategory === TravellerCategory.youth && filteredTicketSuggestions[selectedIndex].youthTogoSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].youthTogoSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                      {currentTravellerCategory === TravellerCategory.adult && filteredTicketSuggestions[selectedIndex].adultOmbudSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].adultOmbudSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                      {currentTravellerCategory === TravellerCategory.youth && filteredTicketSuggestions[selectedIndex].youthOmbudSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].youthOmbudSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                      {currentTravellerCategory === TravellerCategory.adult && filteredTicketSuggestions[selectedIndex].adultOmbordSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].adultOmbordSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                      {currentTravellerCategory === TravellerCategory.youth && filteredTicketSuggestions[selectedIndex].youthOmbordSalesChannelPrice && (
                        <SalesChannelPrice salesChannelPrice={filteredTicketSuggestions[selectedIndex].youthOmbordSalesChannelPrice} onClick={handleSalesChannelClick} />
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </>
          )}
          {filteredTicketSuggestions.length === 0 && <div className="mt-3">{translationNoTicketSuggestionsAvailable}</div>}
        </>
      )}
    </div>
  );
}

export default ProductTypeTable;
